import { useState } from "react";
import { X, ArrowsClockwise, Globe } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export function BulkActionsModal({ open, action, selectedIds, onClose, onDone }) {
    const [totp, setTotp] = useState("");
    const [years, setYears] = useState(1);
    const [nsInput, setNsInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    if (!open) return null;

    const submit = async () => {
        if (totp.length !== 6) return toast.error("Enter your 6-digit TOTP code");
        setBusy(true);
        setResult(null);
        try {
            let data;
            if (action === "renew") {
                ({ data } = await api.post("/domain-actions/bulk-renew", {
                    domain_ids: selectedIds,
                    years,
                    totp_code: totp,
                }));
            } else {
                const ns = nsInput.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
                if (ns.length < 2) {
                    setBusy(false);
                    return toast.error("Provide at least 2 nameservers");
                }
                ({ data } = await api.post("/domain-actions/bulk-nameservers", {
                    domain_ids: selectedIds,
                    nameservers: ns,
                    totp_code: totp,
                }));
            }
            setResult(data);
            toast.success(
                `${action === "renew" ? "Renewed" : "Updated"} ${data.summary.renewed ?? data.summary.updated}, failed ${data.summary.failed}`
            );
            onDone?.();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(15,23,45,0.4)", backdropFilter: "blur(6px)" }}
            onClick={onClose}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="glass-strong w-full max-w-2xl p-8 max-h-[92vh] overflow-auto"
                data-testid="bulk-actions-modal"
            >
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <h2 className="text-2xl font-extrabold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                            {action === "renew" ? "Bulk renew" : "Bulk set nameservers"}
                        </h2>
                        <p className="text-sm text-[color:var(--dm-text-2)] mt-1">
                            <b>{selectedIds.length}</b> domain{selectedIds.length === 1 ? "" : "s"} selected across GoDaddy + Namecheap accounts.
                        </p>
                    </div>
                    <button onClick={onClose} className="btn-ghost !p-2">
                        <X size={18} weight="bold" />
                    </button>
                </div>

                {!result && (
                    <div className="space-y-4">
                        {action === "renew" ? (
                            <label className="block">
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">Years</span>
                                <select value={years} onChange={(e) => setYears(Number(e.target.value))} className="glass-input" data-testid="bulk-renew-years">
                                    {[1, 2, 3, 5, 10].map((y) => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </label>
                        ) : (
                            <label className="block">
                                <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">Nameservers (one per line, min 2)</span>
                                <textarea
                                    value={nsInput}
                                    onChange={(e) => setNsInput(e.target.value)}
                                    className="glass-input mono !min-h-[120px]"
                                    placeholder="ns1.cloudflare.com&#10;ns2.cloudflare.com"
                                    data-testid="bulk-ns-input"
                                />
                            </label>
                        )}

                        <div className="glass !rounded-xl p-3 text-xs bg-[rgba(255,59,48,0.05)] border-[rgba(255,59,48,0.2)]">
                            ⚠️ {action === "renew"
                                ? "This charges the payment method on file for each domain's registrar account."
                                : "This replaces the nameservers on all selected domains."}
                        </div>

                        <div>
                            <span className="text-xs font-semibold text-[color:var(--dm-text-2)] mb-1 block">
                                Enter your 6-digit DomainMigInt TOTP code
                            </span>
                            <input
                                inputMode="numeric"
                                maxLength={6}
                                value={totp}
                                onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                className="glass-input mono text-center text-lg tracking-widest"
                                placeholder="123456"
                                autoFocus
                                data-testid="bulk-totp-input"
                            />
                        </div>

                        <div className="flex justify-end gap-2">
                            <button onClick={onClose} className="btn-ghost">Cancel</button>
                            <button
                                onClick={submit}
                                disabled={busy || totp.length !== 6}
                                className="btn-glossy"
                                data-testid="bulk-submit"
                            >
                                {action === "renew" ? (
                                    <>
                                        <ArrowsClockwise size={14} weight="bold" />
                                        {busy ? "Renewing…" : `Renew ${selectedIds.length}`}
                                    </>
                                ) : (
                                    <>
                                        <Globe size={14} weight="bold" />
                                        {busy ? "Saving…" : `Set NS on ${selectedIds.length}`}
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {result && (
                    <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <div className="glass !rounded-xl p-3">
                                <div className="text-xs uppercase text-[color:var(--dm-text-2)]">Total</div>
                                <div className="text-2xl font-extrabold" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                                    {result.summary.total}
                                </div>
                            </div>
                            <div className="glass !rounded-xl p-3" style={{ background: "rgba(52,199,89,0.12)" }}>
                                <div className="text-xs uppercase text-[color:var(--dm-text-2)]">
                                    {action === "renew" ? "Renewed" : "Updated"}
                                </div>
                                <div className="text-2xl font-extrabold text-[color:var(--dm-success)]" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                                    {result.summary.renewed ?? result.summary.updated}
                                </div>
                            </div>
                            <div className="glass !rounded-xl p-3" style={{ background: "rgba(255,59,48,0.1)" }}>
                                <div className="text-xs uppercase text-[color:var(--dm-text-2)]">Failed</div>
                                <div className="text-2xl font-extrabold text-[color:var(--dm-danger)]" style={{ fontFamily: "Cabinet Grotesk, sans-serif" }}>
                                    {result.summary.failed}
                                </div>
                            </div>
                        </div>
                        <div className="glass !rounded-xl max-h-[50vh] overflow-auto text-xs">
                            <div className="grid grid-cols-[2fr_100px_1fr] gap-2 px-3 py-2 font-bold bg-[rgba(0,122,255,0.05)] sticky top-0">
                                <div>Domain</div>
                                <div>Result</div>
                                <div>Details</div>
                            </div>
                            {result.results.map((r, i) => (
                                <div key={i} className="grid grid-cols-[2fr_100px_1fr] gap-2 px-3 py-1.5 border-b border-[rgba(15,41,89,0.05)]">
                                    <code className="mono">{r.domain || r.domain_id}</code>
                                    <div>
                                        {r.ok ? (
                                            <span className="pill pill-ok">success</span>
                                        ) : (
                                            <span className="pill pill-danger" title={JSON.stringify(r.error)}>failed</span>
                                        )}
                                    </div>
                                    <div className="text-[color:var(--dm-text-2)] truncate" title={JSON.stringify(r.error || "")}>
                                        {r.ok ? (r.order_id || "OK") : (r.error?.message || JSON.stringify(r.error) || "").slice(0, 80)}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-end">
                            <button onClick={onClose} className="btn-glossy" data-testid="bulk-done">Done</button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
